// Which entities are worth re-rendering the app for.
//
// MEASURED 2026-07-29 against the live house, before writing any of
// this. In a 10-minute window 733 entities pushed an update and 17 of
// them were entities this panel reads; of the 131 that actually changed
// state, none were. Over an hour: 1022 reporters, 41 watched. So ~97%
// of full-app re-renders were for entities that appear nowhere in the
// UI — 3,239 entities exist and the panel watches 141 of them (measured
// in-browser against the real MAPPINGS; 111 come from MAPPINGS itself,
// the rest from SONOS_SPEAKERS, guest mode and the dynamic per-room ids).
//
// (The TODO that prompted this guessed the culprits were uptime sensors
// and the Kitchen Apple-TV flap. Uptime sensors were 7 of the 733. The
// real answer is simply that the house is large and the panel is small.)
//
// The set is DERIVED from MAPPINGS rather than hand-listed, so adding a
// room or repointing a tile extends it automatically. A hand-maintained
// copy would drift, and the failure mode of drift here is a tile that
// silently stops updating — the same class of bug as the Office lamp
// and the Pavilion climate tile.

// Deliberately NO import of use-panel-rooms.jsx. The caller passes
// MAPPINGS in. Node's ESM loader rejects a `.jsx` extension outright,
// so a module that imports one cannot be unit-tested under plain
// `node` — which is exactly why dev/intent-settle.test.mjs and
// dev/cover-motion.test.mjs currently cannot run at all. Keeping this
// file free of `.jsx` imports is what lets watch-set.test.mjs exist.
// A plain `.js` import is fine, hence vacuum/policy.js below.

import { VACUUM_ENTITIES } from '../vacuum/policy.js';

// Entities the app reads but MAPPINGS does not name, because the code
// builds them from the room id at the call site. Keep this in step with
// tv/shell.jsx and tv/source-picker.jsx.
const DYNAMIC_SUFFIXES = ['_tv_2', '_tv_cast'];
const DYNAMIC_DOMAINS = { '_tv_2': ['media_player', 'remote'], '_tv_cast': ['media_player'] };

// Walk any nested shape and collect anything that looks like an
// entity_id. Deliberately structure-agnostic: MAPPINGS nests
// differently per room (lights arrays, screen/audio/av objects, amp
// blocks), and a walker cannot fall out of step with that the way an
// explicit per-key reader would.
function collectIds(node, out) {
  if (node == null) return out;
  if (typeof node === 'string') {
    if (/^[a-z_]+\.[a-z0-9_]+$/.test(node)) out.add(node);
    return out;
  }
  if (Array.isArray(node)) {
    node.forEach((n) => collectIds(n, out));
    return out;
  }
  if (typeof node === 'object') {
    Object.values(node).forEach((n) => collectIds(n, out));
    return out;
  }
  return out;
}

// Entities read outside MAPPINGS entirely. Found 2026-07-29 by grepping
// every `hass.states[...]` reader rather than trusting MAPPINGS to be
// the whole story — it is not, and a filter built from MAPPINGS alone
// would have quietly frozen both of these surfaces.
export const GUEST_MODE_ENTITIES = [
  'input_boolean.guest_mode',           // read in app.jsx AND guest-mode/shell.jsx
  'input_datetime.guest_mode_start',
  'input_datetime.guest_mode_end',
];

// `mappings` supplies the room ids for the dynamic suffixes; every
// other argument is just walked for entity ids. Pass anything the app
// reads — SONOS_SPEAKERS, guest mode, whatever comes next.
export function buildWatchSet(mappings, ...extras) {
  const out = new Set();
  collectIds(mappings, out);
  extras.forEach((e) => collectIds(e, out));
  GUEST_MODE_ENTITIES.forEach((id) => out.add(id));
  // Vacuum: same story as guest mode. Nothing in MAPPINGS names the
  // robot, so without this the spot-clean surface and its room-tile
  // cards would render once and then freeze — the failure mode this
  // whole module was written to avoid. Sourced from vacuum/policy.js
  // rather than copied, because a second hand-maintained copy is
  // exactly the drift described above.
  VACUUM_ENTITIES.forEach((id) => out.add(id));
  Object.keys(mappings || {}).forEach((roomId) => {
    DYNAMIC_SUFFIXES.forEach((suffix) => {
      DYNAMIC_DOMAINS[suffix].forEach((domain) => {
        out.add(`${domain}.${roomId}${suffix}`);
      });
    });
  });
  return out;
}

// Did anything we care about actually move?
//
// subscribeEntities hands back a fresh map but preserves object
// identity for entities that did not change, so a reference check per
// watched entity is both exact and cheap (~141 comparisons). Returns
// true on first call (no previous map) and whenever the watch set is
// empty, so the caller fails OPEN — if this module is wrong, the app
// re-renders too often, which is what it did before.
export function watchedChanged(prev, next, watch) {
  if (!prev || !next) return true;
  if (!watch || watch.size === 0) return true;
  for (const id of watch) {
    if (prev[id] !== next[id]) return true;
  }
  return false;
}
